// /admin/challenges — realtime customer challenge plane viewer.
//
// Backed by /api/v1/admin/challenges* on the case-service. Read-only:
// the actual lifecycle mutations (issue / verify / expire) all happen
// channel-side or via the janitor; this surface is operator
// visibility into what the challenge plane has been doing.
//
// See documentation/realtime-customer-challenge-plane.md for the
// architectural contract.

import { apiJson } from '@/api/client'

export type ChallengeStatus =
  | 'issued'
  | 'delivered'
  | 'passed'
  | 'failed'
  | 'expired'
  | 'undeliverable'

export type ChallengeType = 'otp' | 'liveness' | 'in_app_approval'

export type ChallengeDelivery = 'push' | 'sms' | 'inline' | 'none'

export type ChallengeTriggerType =
  | 'threshold'
  | 'rule'
  | 'list'
  | 'analyst'
  | 'scenario'

export interface ChallengeHold {
  challenge_id: string
  decision_id: number
  channel: string
  type: ChallengeType
  delivery: ChallengeDelivery
  status: ChallengeStatus
  attempts: number
  max_attempts: number
  attempts_remaining: number
  expires_at: string
  delivered_at?: string
  verified_at?: string
  fail_reason?: string
  created_at: string
  updated_at: string
  has_liveness_session: boolean
  has_device_key: boolean
  // Trigger attribution. The Source column on /admin/challenges
  // links rule-driven holds back to /admin/rules/<rule_id>.
  triggered_by: ChallengeTriggerType
  triggered_by_source?: string
  triggered_by_rule_id?: number
  triggered_by_rule_public_id?: string
  // Forensic chain — link to the alert, transaction, and customer
  // when the joins resolved at admin/list time.
  linked_alert_id?: string
  linked_alert_number?: string
  linked_alert_count: number
  linked_transaction_public_id?: string
  linked_customer_public_id?: string
  linked_customer_number?: string
}

export interface ChallengesListResponse {
  holds: ChallengeHold[]
  count: number
  total: number
  limit: number
  offset: number
}

export interface ChallengesListParams {
  status?: ChallengeStatus
  inFlight?: boolean
  channel?: string
  type?: ChallengeType
  triggeredBy?: ChallengeTriggerType
  ruleID?: number
  rule?: string
  /** Narrow to challenges raised on a specific customer's transactions. */
  customerID?: string
  /** Narrow to challenges raised on the same decision as this alert. */
  alertID?: string
  q?: string
  from?: string // RFC3339 timestamp
  to?: string   // RFC3339 timestamp
  sinceMinutes?: number
  limit?: number
  offset?: number
}

export function listChallenges(params: ChallengesListParams = {}) {
  const q = new URLSearchParams()
  if (params.inFlight) q.set('in_flight', 'true')
  else if (params.status) q.set('status', params.status)
  if (params.channel) q.set('channel', params.channel)
  if (params.type) q.set('type', params.type)
  if (params.triggeredBy) q.set('triggered_by', params.triggeredBy)
  if (params.ruleID != null) q.set('rule_id', String(params.ruleID))
  if (params.rule) q.set('rule', params.rule)
  if (params.customerID) q.set('customer_id', params.customerID)
  if (params.alertID) q.set('alert_id', params.alertID)
  if (params.q) q.set('q', params.q)
  if (params.from) q.set('from', params.from)
  if (params.to) q.set('to', params.to)
  if (params.sinceMinutes != null) q.set('since_minutes', String(params.sinceMinutes))
  if (params.limit != null) q.set('limit', String(params.limit))
  if (params.offset != null) q.set('offset', String(params.offset))
  const qs = q.toString()
  return apiJson<ChallengesListResponse>(
    `/admin/challenges${qs ? `?${qs}` : ''}`,
  )
}

// ---- Per-rule trigger stats ---------------------------------------

export interface RuleTriggerStats {
  rule_id: number
  rule_public_id?: string
  rule_name: string
  total: number
  passed: number
  failed: number
  expired: number
  undeliverable: number
  in_flight: number
  pass_rate: number | null
  avg_duration_ms: number | null
}

export interface TriggerStatsResponse {
  rules: RuleTriggerStats[]
  since_hours: number
  generated_at: string
}

export function getTriggerStats(sinceHours = 24, limit = 50) {
  const q = new URLSearchParams({
    since_hours: String(sinceHours),
    limit: String(limit),
  })
  return apiJson<TriggerStatsResponse>(
    `/admin/challenges/triggers/stats?${q.toString()}`,
  )
}

export function getChallenge(challengeID: string) {
  return apiJson<ChallengeHold>(
    `/admin/challenges/${encodeURIComponent(challengeID)}`,
  )
}

// ChallengeTransitionEvent is the WS payload pushed by the
// live-broadcaster when a hold reaches a terminal state. Subscribe
// via useWsSubscription('challenge.transition', …).
export interface ChallengeTransitionEvent {
  challenge_id: string
  decision_id: number
  channel: string
  challenge_type: ChallengeType
  status: ChallengeStatus
  attempts: number
  duration_ms?: number
  fail_reason?: string
  occurred_at: string
}

// ---- Plane settings (vendor pickers + tuning) ----------------------
//
// The realtime customer challenge plane reads the singleton
// config.challenge_plane_settings row at issue time. Operators flip
// the active liveness vendor (Dojah ↔ Smile Identity ↔ Premblly)
// and tune scoring / deadline knobs from this surface.

export interface ChallengePlaneSettings {
  liveness_integration_name?: string
  sms_integration_name?: string
  push_integration_name?: string
  liveness_min_score: number
  liveness_face_match_min_score: number
  otp_deadline_seconds: number | null
  liveness_deadline_seconds: number | null
  in_app_approval_deadline_seconds: number | null
  otp_max_attempts: number | null
  liveness_max_attempts: number | null
  in_app_approval_max_attempts: number | null
  updated_at: string
}

export type ChallengePlaneSettingsPatch = Partial<{
  liveness_integration_name: string
  sms_integration_name: string
  push_integration_name: string
  liveness_min_score: number
  liveness_face_match_min_score: number
  otp_deadline_seconds: number
  liveness_deadline_seconds: number
  in_app_approval_deadline_seconds: number
  otp_max_attempts: number
  liveness_max_attempts: number
  in_app_approval_max_attempts: number
}>

export function getChallengePlaneSettings() {
  return apiJson<ChallengePlaneSettings>('/admin/challenge-plane-settings')
}

export function patchChallengePlaneSettings(patch: ChallengePlaneSettingsPatch) {
  return apiJson<ChallengePlaneSettings>('/admin/challenge-plane-settings', {
    method: 'PATCH',
    body: patch,
  })
}
